import React, {useState, useEffect, useRef} from "react";
import {
  TextField,
  MenuItem,
  FormControl,
  InputAdornment,
  IconButton,
  Paper,
  ClickAwayListener,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ClearIcon from "@mui/icons-material/Clear";
import style from "../../assets/css/global-fields.module.scss";

const GlobalSearchDropdown = ({
  options = [],
  value = "",
  onChange,
  placeH = "Search/Type...",
  id,
  name,
  label,
  error,
  helperText,
  fullWidth = true,
  disabled = false,
  cssClass,
  stateField = false,
  fetchStates,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [openMenu, setOpenMenu] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const inputRef = useRef(null);
  const listRef = useRef(null);
  const prevValueRef = useRef(value);

  // Reset dropdown if value is externally cleared
  if (value === "" && prevValueRef.current !== "") {
    setInputValue("");
    setHighlightedIndex(-1);
    setOpenMenu(false);
    prevValueRef.current = "";
  } else {
    prevValueRef.current = value;
  }

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(inputValue.toLowerCase())
  );
  // console.log("filteredOptions",options,filteredOptions)
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const menuItems = listRef.current.querySelectorAll("li.MuiMenuItem-root");
      if (menuItems[highlightedIndex]) {
        menuItems[highlightedIndex].scrollIntoView({
          block: "nearest",
        });
      }
    }
  }, [highlightedIndex]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    setOpenMenu(true);
    setHighlightedIndex(-1);
    onChange?.({target: {name, value: val}});
  };

  const handleClear = () => {
    setInputValue("");
    setHighlightedIndex(-1);
    onChange?.({target: {name, value: ""}});
    setOpenMenu(false);
  };

  const handleSelect = (val) => {
    setInputValue(val);
    setHighlightedIndex(-1);
    onChange?.({target: {name, value: val}});
    setOpenMenu(false);
  };

  const handleKeyDown = (e) => {
    if (!openMenu && e.key === "ArrowDown") {
      setOpenMenu(true);
    }

    if (openMenu) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
      } else if (e.key === "Enter") {
        // if (highlightedIndex >= 0) {
        //   e.preventDefault();
        //   const selectedOption = filteredOptions[highlightedIndex];
        //   handleSelect(selectedOption.value);
        // }
        e.preventDefault();
        const indexToSelect =
          highlightedIndex >= 0
            ? highlightedIndex
            : filteredOptions.length > 0
              ? 0
              : -1;

        if (indexToSelect >= 0) {
          const selectedOption = filteredOptions[indexToSelect];
          handleSelect(selectedOption.value);
        }
      } else if (e.key === "Escape") {
        setOpenMenu(false);
        setHighlightedIndex(-1);
      }
    }
  };

  return (
    <div className={style.proposalTextField}>
      <FormControl fullWidth={fullWidth} className="TextFieldPosiRela">
        <ClickAwayListener onClickAway={() => setOpenMenu(false)}>
          <div>
            <TextField
              fullWidth={fullWidth}
              id={id}
              name={name}
              label={label}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={placeH}
              error={error}
              helperText={helperText}
              disabled={disabled}
              className={cssClass}
              inputRef={inputRef}
              variant="outlined"
              autoComplete="off"
              InputProps={{
                endAdornment: (
                  <>
                    {inputValue && (
                      <InputAdornment position="end">
                        <IconButton
                          disabled={disabled}
                          onClick={handleClear}
                          edge="end"
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    )}
                    <InputAdornment position="end">
                      <IconButton
                        disabled={disabled}
                        onClick={() => {
                          if (
                            stateField &&
                            (!options || options.length === 0)
                          ) {
                            fetchStates();
                            setOpenMenu((prev) => !prev);
                          } else {
                            setOpenMenu((prev) => !prev);
                          }
                        }}
                        edge="end"
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </InputAdornment>
                  </>
                ),
              }}
            />
            {openMenu && filteredOptions.length > 0 && (
              <Paper
                ref={listRef}
                style={{
                  position: "absolute",
                  zIndex: 9999999,
                  width: "100%",
                  maxHeight: 150,
                  overflowY: "auto",
                }}
              >
                {filteredOptions.map((option, idx) => (
                  <MenuItem
                    key={idx}
                    selected={idx === highlightedIndex}
                    onClick={() => handleSelect(option.value)}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Paper>
            )}
          </div>
        </ClickAwayListener>
      </FormControl>
    </div>
  );
};

export default GlobalSearchDropdown;
